import React from 'react';
import { Headphones, BookOpen, Image as ImageIcon, Film, Lock, ChevronRight } from 'lucide-react';
import RaccontoPlayer from '../racconti/RaccontoPlayer';

// Phase F6/F7 — Racconti audio + testo first (Voice-First), legacy media last.
const TYPE_META = {
  audio: { Icon: Headphones, fallback: 'Racconto audio', cta: 'Apri' },
  text: { Icon: BookOpen, fallback: 'Racconto scritto', cta: 'Leggi' },
  image: { Icon: ImageIcon, fallback: 'Foto', cta: 'Guarda' },
  video: { Icon: Film, fallback: 'Video', cta: 'Guarda' },
};
const TYPE_ORDER = { audio: 0, text: 0, image: 1, video: 2 };

const rowTitle = (p) => {
  if (p.title) return p.title;
  const line = (p.caption || '').split('\n')[0].trim();
  return line || TYPE_META[p.media_type]?.fallback || 'Racconto';
};

const fmtDur = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;
const readMinutes = (t) => Math.max(1, Math.round(((t || '').trim().split(/\s+/).length || 1) / 200));

const excerpt = (t) => {
  const clean = (t || '').replace(/\s+/g, ' ').trim();
  return clean.length > 120 ? `${clean.slice(0, 120)}…` : clean;
};

/**
 * RaccontiSection — Phase F5/F6/F7 Vetrina. Compact Voice-First list (NO
 * social grid). Free/unlocked audio embeds the shared RaccontoPlayer inline
 * (Play/Pausa, seek, velocità) — Phase F7. Locked premium audio renders NO
 * player and never touches the media (server gating: media_url is empty).
 * Voice-First profile: optional `types` filter (e.g. ['audio'] / ['text'])
 * so audio and text get dedicated tabs and legacy photo/video posts are
 * simply NOT presented here (data stays untouched in DB/storage).
 */
const RaccontiSection = ({
  posts, username, onOpen,
  types = null, title = 'Racconti', emptyText = null,
  testId = 'vetrina-racconti-section',
}) => {
  const source = types ? posts.filter((p) => types.includes(p.media_type)) : posts;
  const sorted = [...source].sort(
    (a, b) => (TYPE_ORDER[a.media_type] ?? 3) - (TYPE_ORDER[b.media_type] ?? 3),
  );
  return (
    <section className="mt-6" data-testid={testId}>
      {title && (
        <h2 className="mb-3 text-sm font-bold uppercase tracking-wider gold-text">
          {title}
        </h2>
      )}
      {sorted.length === 0 ? (
        <p className="rounded-xl border border-border bg-card px-4 py-6 text-center text-xs text-muted-foreground" data-testid={`${testId}-empty`}>
          {emptyText || `@${username} non ha ancora pubblicato racconti.`}
        </p>
      ) : (
        <div className="flex flex-col gap-2">
          {sorted.map((p) => {
            const meta = TYPE_META[p.media_type] || TYPE_META.audio;
            const lockedPost = p.is_paid && !p.has_access;
            const playable = p.media_type === 'audio' && !lockedPost && !!p.media_url;
            return (
              <div
                key={p.id}
                className="rounded-xl border border-border bg-card px-3 py-3 transition-colors hover:border-amber-500/40"
                data-testid={`racconto-row-${p.id}`}
              >
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => onOpen(p)}
                  onKeyDown={(e) => { if (e.key === 'Enter') onOpen(p); }}
                  className="flex w-full cursor-pointer items-center gap-3 text-left"
                  data-testid={`racconto-open-${p.id}`}
                >
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-amber-500/10 gold-text">
                    <meta.Icon className="h-4 w-4" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="line-clamp-1 text-sm font-semibold">{rowTitle(p)}</span>
                    {p.media_type === 'text' && !lockedPost && p.body_text && (
                      <span className="mt-0.5 line-clamp-2 text-xs text-muted-foreground" data-testid={`racconto-excerpt-${p.id}`}>
                        {excerpt(p.body_text)}
                      </span>
                    )}
                    <span className="mt-0.5 flex items-center gap-2 text-[11px] text-muted-foreground">
                      {p.media_type === 'audio' && p.duration_seconds > 0 && (
                        <span className="tabular-nums" data-testid={`racconto-duration-${p.id}`}>{fmtDur(p.duration_seconds)}</span>
                      )}
                      {p.media_type === 'text' && p.body_text && (
                        <span data-testid={`racconto-reading-${p.id}`}>{readMinutes(p.body_text)} min lettura</span>
                      )}
                      {p.is_paid ? (
                        <span className="flex items-center gap-1 font-semibold gold-text" data-testid={`racconto-price-${p.id}`}>
                          <Lock className="h-3 w-3" /> {p.price} Crediti
                        </span>
                      ) : (
                        <span className="font-semibold text-emerald-500" data-testid={`racconto-free-${p.id}`}>Gratis</span>
                      )}
                    </span>
                  </span>
                  <span className="flex shrink-0 items-center gap-1 rounded-full border border-border px-3 py-1 text-[11px] font-semibold">
                    {lockedPost ? (p.media_type === 'text' ? 'Leggi con crediti' : 'Ascolta con crediti') : meta.cta}
                    <ChevronRight className="h-3 w-3" />
                  </span>
                </div>
                {/* Phase F7 — inline player: only for accessible audio.
                    Lazy media: no network until the user presses Play. */}
                {playable && <RaccontoPlayer post={p} className="mt-2.5" />}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
};

export default RaccontiSection;
